import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import { useCaseProcessing } from '@/hooks/useCaseProcessing';
import { useInsightsStatus } from '@/hooks/useInsightsStatus';
import { CASE_STATUS, CASE_STATUS_TEXT } from '@/utils/cases';

import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { FetchStates } from '@/components/fetch-states';
import { LoadingDots } from '@/components/ui/loading-dots';
import { ProcessingStatusBanner } from '@/components/cases/procesing-status-banner';

import { ImageUploader } from './drag-images';
import { ImagesList } from './images-list';

type ProcessCaseProps = {
  caseId: string;
  isNewCase?: boolean;
};

const MIN_IMAGE_COUNT = 2;
const ENABLE_IA_PROCESSING_FEEDBACK = false;

const isCaseProcessing = (status?: number) => {
  if (!status) return false;
  return status === CASE_STATUS.IN_PROGRESS || status === CASE_STATUS.PENDING;
};

export function ProcessCase(props: ProcessCaseProps) {
  const { caseId, isNewCase } = props;
  const [showInsightsBanner, setShowInsightsBanner] = useState(true);

  const { t } = useTranslation();

  const {
    imageList,
    caseResult,
    isLoadingImages,
    sendingToProcessing,
    handleFileDelete,
    handleUploadSuccess,
    initProcess,
  } = useCaseProcessing({ caseId, isNewCase });

  // Use insights status hook to monitor AI processing progress
  const { data: insightsData, isComplete } = useInsightsStatus({
    caseId,
    enabled: sendingToProcessing && caseResult !== null,
  });

  // Close banner when insights processing completes
  useEffect(() => {
    if (isComplete && showInsightsBanner) {
      // Wait 3 seconds after completion before hiding the banner
      const timer = setTimeout(() => {
        setShowInsightsBanner(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isComplete, showInsightsBanner]);

  return (
    <div className="CreateCaseForm cell small-12 medium-9">
      <h2 className="title">Process the case</h2>
      <p className="case-id fineprint mb-8">
        {t('cases-table-case-id')}: {caseId}
      </p>
      <FetchStates
        loading={isLoadingImages}
        empty={false}
        loadingMessage={t('case-create-loading-images')}
      >
        {caseResult && ENABLE_IA_PROCESSING_FEEDBACK ? (
          <Banner type={isCaseProcessing(caseResult) ? 'warning' : 'success'} enableClose={false}>
            <div className="w-full flex justify-between items-center">
              <div className="inline-flex gap-2">
                {t('case-create-current-status') + ': '}
                <span className="font-bold">
                  {(isCaseProcessing(caseResult)
                    ? t('case-create-processing')
                    : t('case-create-processed')) +
                    ` | Status (${caseResult}) - ${CASE_STATUS_TEXT[caseResult]}`}
                </span>
              </div>
              {isCaseProcessing(caseResult) ? <LoadingDots background="bg-white" /> : null}
            </div>
          </Banner>
        ) : null}
        {/* Insights Processing Status Banner */}
        {insightsData && showInsightsBanner && (
          <div className="mb-4">
            <ProcessingStatusBanner
              images={insightsData.data.map((img) => ({
                name: img.image_filename,
                status: img.status === 'not_started' ? 'not started' : img.status,
              }))}
            />
          </div>
        )}
        <ImageUploader
          caseId={caseId}
          images={imageList}
          onUploadSuccess={handleUploadSuccess}
          disabled={caseResult !== null || sendingToProcessing}
        />
        <ImagesList
          caseId={caseId}
          images={imageList}
          onRemoveImage={handleFileDelete}
          disabled={caseResult !== null || sendingToProcessing}
        />
        {caseResult === null ? (
          <Button
            disabled={imageList.length < MIN_IMAGE_COUNT}
            loading={sendingToProcessing}
            onClick={() => void initProcess()}
            id="submit"
            data-tooltip-id="submit"
          >
            {t('submit')}
          </Button>
        ) : null}
        <Tooltip id="submit" place="top">
          {imageList.length < MIN_IMAGE_COUNT && (
            <p className="text-white text-sm">{t('case-create-image-directions')}</p>
          )}
        </Tooltip>
      </FetchStates>
    </div>
  );
}
